import { useCallback, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { assistantApi } from '../services/api';
import { getSocketBaseUrl } from '../utils/runtimeUrls';

const SOCKET_URL = getSocketBaseUrl();

const SERVICE_WORKER_URL = process.env.NODE_ENV === 'production'
  ? `${process.env.PUBLIC_URL || ''}/service-worker.js`
  : `${process.env.PUBLIC_URL || ''}/dev-service-worker.js`;

const PUSH_PUBLIC_KEY_STORAGE_KEY = 'assistant_push_public_key';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
};

const toUint8Array = (value) => {
  if (!value) return null;
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  return null;
};

const areUint8ArraysEqual = (left, right) => {
  if (!left || !right || left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
};

const subscriptionMatchesPublicKey = (subscription, publicKey) => {
  try {
    const subscriptionKey = toUint8Array(subscription?.options?.applicationServerKey);
    if (!subscriptionKey) return false;
    return areUint8ArraysEqual(subscriptionKey, urlBase64ToUint8Array(publicKey));
  } catch (_) {
    return false;
  }
};

const ensureServiceWorkerRegistration = async () => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  const existingRegistration = await navigator.serviceWorker.getRegistration();
  if (existingRegistration) {
    return existingRegistration;
  }

  try {
    return await navigator.serviceWorker.register(SERVICE_WORKER_URL);
  } catch (error) {
    throw new Error(error?.message || error?.name || 'service_worker_registration_failed');
  }
};

export default function useAssistantNotifications({ onTaskEvent } = {}) {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );
  const [pushBusy, setPushBusy] = useState(false);
  const pushSupported = typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && typeof Notification !== 'undefined';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = io(`${SOCKET_URL}/assistant`, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionAttempts: Infinity,
      auth: { token },
    });

    socket.on('connect', () => {
      setConnectionStatus('connected');
    });

    socket.on('disconnect', () => {
      setConnectionStatus('disconnected');
    });

    socket.on('connect_error', () => {
      setConnectionStatus('error');
    });

    socket.on('assistant_ready', () => {
      setConnectionStatus('connected');
    });

    socket.on('task_created', (payload) => {
      if (typeof onTaskEvent === 'function') {
        onTaskEvent('task_created', payload);
      }
    });

    socket.on('task_updated', (payload) => {
      if (typeof onTaskEvent === 'function') {
        onTaskEvent('task_updated', payload);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [onTaskEvent]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return undefined;
    }

    const handleMessage = (event) => {
      if (typeof onTaskEvent === 'function' && event?.data?.type) {
        onTaskEvent(event.data.type, event.data);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [onTaskEvent]);

  const syncPushSubscription = useCallback(async ({ requestPermission = false } = {}) => {
    if (!pushSupported) {
      if (requestPermission) {
        throw new Error('push_not_supported');
      }
      return { skipped: true, reason: 'push_not_supported' };
    }

    const configResponse = await assistantApi.getPushPublicKey();
    const pushConfig = configResponse.data || {};
    if (!pushConfig.enabled || !pushConfig.publicKey) {
      if (requestPermission) {
        throw new Error('push_not_configured');
      }
      return { skipped: true, reason: 'push_not_configured' };
    }

    let permission = Notification.permission;
    if (requestPermission && permission !== 'granted') {
      permission = await Notification.requestPermission();
    }
    setNotificationPermission(permission);

    if (permission !== 'granted') {
      if (requestPermission) {
        throw new Error(permission === 'denied' ? 'permission_denied' : 'permission_not_granted');
      }
      return { skipped: true, reason: permission };
    }

    const registration = await ensureServiceWorkerRegistration();
    if (!registration) {
      if (requestPermission) {
        throw new Error('service_worker_unavailable');
      }
      return { skipped: true, reason: 'service_worker_unavailable' };
    }

    let subscription = await registration.pushManager.getSubscription();
    const storedPublicKey = localStorage.getItem(PUSH_PUBLIC_KEY_STORAGE_KEY);
    const hasMatchingKey = subscription && (
      subscriptionMatchesPublicKey(subscription, pushConfig.publicKey)
      || storedPublicKey === pushConfig.publicKey
    );

    if (subscription && !hasMatchingKey) {
      try {
        await assistantApi.unsubscribePush({ endpoint: subscription.endpoint });
      } catch (_) {
        // best-effort cleanup lato backend
      }

      await subscription.unsubscribe();
      subscription = null;
    }

    if (!subscription) {
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pushConfig.publicKey),
        });
      } catch (error) {
        const reason = error?.message || error?.name || 'push_subscription_failed';
        if (requestPermission) {
          throw new Error(reason);
        }
        return { skipped: true, reason };
      }
    }

    await assistantApi.subscribePush({
      subscription: subscription.toJSON ? subscription.toJSON() : subscription,
    });

    localStorage.setItem(PUSH_PUBLIC_KEY_STORAGE_KEY, pushConfig.publicKey);

    return { success: true };
  }, [pushSupported]);

  useEffect(() => {
    if (!pushSupported || notificationPermission !== 'granted') {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        await syncPushSubscription();
      } catch (error) {
        if (!cancelled) {
          console.warn('[Assistant Push] Auto sync fallita:', error?.message || error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notificationPermission, pushSupported, syncPushSubscription]);

  const enablePush = useCallback(async () => {
    if (!pushSupported) {
      throw new Error('push_not_supported');
    }

    setPushBusy(true);
    try {
      return await syncPushSubscription({ requestPermission: true });
    } finally {
      setPushBusy(false);
    }
  }, [pushSupported, syncPushSubscription]);

  const disablePush = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      localStorage.removeItem(PUSH_PUBLIC_KEY_STORAGE_KEY);
      return { success: true, removed: 0 };
    }

    setPushBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = registration ? await registration.pushManager.getSubscription() : null;
      if (subscription) {
        await assistantApi.unsubscribePush({ endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      localStorage.removeItem(PUSH_PUBLIC_KEY_STORAGE_KEY);
      return { success: true, removed: subscription ? 1 : 0 };
    } finally {
      setPushBusy(false);
    }
  }, []);

  const sendTestPush = useCallback(async () => {
    return assistantApi.testPush();
  }, []);

  return {
    connectionStatus,
    notificationPermission,
    pushBusy,
    pushSupported,
    enablePush,
    disablePush,
    sendTestPush,
  };
}